import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAbility } from '@casl/react';
import {
    AlertTriangle, ClipboardCheck, ClipboardList, FileText, Package, Plus, Wallet,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Can } from '../../../components/Can';
import AccessRestricted from '../../../components/AccessRestricted';
import { AbilityContext } from '../../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../../constants/permissions.constants';
import { DashboardAttentionItem, ProcurementDashboard, PurchaseOrderScope } from '../../../types/procurement.types';
import { procurementDashboardApi } from '../../../utils/procurement-api';
import { formatDateTime, money } from './procurement-ui';
import { cn } from '../../../components/ui/utils';

/** Dot colour per attention kind — the same loud/quiet vocabulary the status pills use. */
const ATTENTION_DOT: Record<DashboardAttentionItem['kind'], string> = {
    approval: 'bg-amber-500',
    entry: 'bg-blue-600',
    receipt: 'bg-slate-900',
    rejected: 'bg-red-600',
    draft: 'bg-gray-400',
};

interface MetricCardProps {
    label: string;
    value: string;
    hint: string;
    href: string;
    icon: React.ReactNode;
    /** The icon chip's own colour — one hue per metric, as the prototype's cards do. */
    iconClass: string;
    /** The caller's own queue — amber ring and border, not another grey tile. */
    loud?: boolean;
}

function MetricCard({ label, value, hint, href, icon, iconClass, loud }: MetricCardProps) {
    return (
        <Link to={href} className="block">
            <Card
                className={cn(
                    'h-full transition-colors hover:border-slate-300',
                    loud && 'border-amber-300 ring-2 ring-amber-100'
                )}
            >
                <CardContent className="p-4">
                    {/* Label left, coloured icon chip right, figure under it — the prototype's
                        card anatomy, so the five metrics read as one row of instruments. */}
                    <div className="flex items-start justify-between gap-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            {label}
                        </span>
                        <span className={cn(
                            'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white',
                            iconClass
                        )}>
                            {icon}
                        </span>
                    </div>
                    <p className={cn('text-2xl font-bold mt-2 tabular-nums', value === '0' && 'text-muted-foreground')}>
                        {value}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">{hint}</p>
                </CardContent>
            </Card>
        </Link>
    );
}

/**
 * Subtitle per server-resolved scope. Every figure on the page is filtered by the same scope, so
 * the heading has to say whose numbers these are — a Requester must not read "every branch"
 * above totals that are only their own.
 */
const SCOPE_SUBTITLE: Record<PurchaseOrderScope, string> = {
    all: 'Requests, approvals and receiving across every branch.',
    own: "Your purchase order requests and how they're progressing.",
    approval: 'Purchase orders waiting on your decision.',
    receiving: 'Deliveries to book in against your yards’ purchase orders.',
    mixed: 'Your requests, plus the queues your access covers.',
};

/**
 * Procurement dashboard — the prototype's five metrics plus the two cards that actually get
 * worked from: what needs this user today, and what the branch has been doing.
 *
 * Which card is "loud" is decided by the caller's own grants rather than by the number:
 * a pending-approval queue is only this person's problem if they can approve.
 */
export default function ProcurementDashboardPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.PURCHASE_ORDER);
    const canApprove = ability.can(ACTIONS.APPROVE, RESOURCES.PURCHASE_ORDER_APPROVAL);
    const canEnter = ability.can(ACTIONS.CREATE, RESOURCES.PURCHASE_ORDER_ENTRY);
    const navigate = useNavigate();

    const [data, setData] = useState<ProcurementDashboard | null>(null);
    const [loading, setLoading] = useState(true);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setData(await procurementDashboardApi.get());
            setError(null);
        } catch (err) {
            // Already toasted by the api client; kept so the page can explain its own silence.
            setError(err instanceof Error && err.message
                ? err.message
                : 'Failed to load the procurement dashboard');
        } finally {
            setLoading(false);
            setHasLoaded(true);
        }
    }, []);

    useEffect(() => { if (canRead) load(); }, [canRead, load]);

    if (!canRead) return <AccessRestricted />;

    if (loading && !hasLoaded) {
        return (
            <div className="w-full space-y-4 animate-pulse">
                {[0, 1, 2].map((i) => <div key={i} className="h-28 bg-gray-300 rounded-lg" />)}
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="w-full">
                <Card className="border-dashed">
                    <CardContent className="p-8 text-center">
                        <AlertTriangle size={28} className="mx-auto text-destructive mb-3" />
                        <p className="font-medium">Could not load the procurement dashboard</p>
                        <p className="text-sm text-muted-foreground mt-1">{error}</p>
                        <Button variant="outline" className="mt-4" onClick={load}>Try again</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!data) return null;

    const { counts, committedSpend, attention, recentActivity } = data;
    const approvalsLoud = canApprove && counts.pendingApprovals > 0;
    const enteringLoud = canEnter && counts.readyToEnter > 0;

    return (
        <div className="w-full space-y-4 md:space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Purchase Orders</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {SCOPE_SUBTITLE[data.scope] ?? SCOPE_SUBTITLE.all}
                    </p>
                </div>
                <Can I={ACTIONS.CREATE} a={RESOURCES.PURCHASE_ORDER}>
                    <Button
                        onClick={() => navigate('/stock/procurement/purchase-orders/new')}
                        className="flex items-center gap-2 w-full md:w-auto bg-slate-900 hover:bg-slate-800 text-white border-0"
                    >
                        <Plus size={16} /><span>New purchase order</span>
                    </Button>
                </Can>
            </div>

            {/* Five metrics in one row once there is room, wrapping down to one per row on a
                phone — the prototype's auto-fit behaviour without the arbitrary width maths. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 md:gap-4">
                <MetricCard
                    label="Open requests"
                    value={String(counts.openRequests)}
                    hint="Draft, submitted or waiting on approval"
                    href="/stock/procurement/purchase-orders"
                    icon={<FileText size={16} />}
                    iconClass="bg-slate-500"
                />
                <MetricCard
                    label="Pending approvals"
                    value={String(counts.pendingApprovals)}
                    hint={counts.pendingApprovals > 0
                        ? `Oldest ${counts.oldestPendingDays} day${counts.oldestPendingDays === 1 ? '' : 's'} old`
                        : 'Queue is clear'}
                    href="/stock/procurement/approvals"
                    icon={<ClipboardCheck size={16} />}
                    iconClass="bg-amber-500"
                    loud={approvalsLoud}
                />
                <MetricCard
                    label="In receiving"
                    value={String(counts.inReceiving)}
                    hint="Issued or part-delivered POs"
                    href="/stock/procurement/purchase-orders?status=receiving"
                    icon={<Package size={16} />}
                    iconClass="bg-slate-900"
                />
                <MetricCard
                    label="Ready to enter"
                    value={String(counts.readyToEnter)}
                    hint="Waiting on AP data entry"
                    href="/stock/procurement/purchase-orders?status=ready_to_enter"
                    icon={<ClipboardList size={16} />}
                    iconClass="bg-blue-600"
                    loud={enteringLoud}
                />
                <MetricCard
                    label="Committed spend"
                    value={money(committedSpend)}
                    hint="Issued, receiving and ready to enter"
                    href="/stock/procurement/purchase-orders"
                    icon={<Wallet size={16} />}
                    iconClass="bg-emerald-600"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                    <CardContent className="p-4 md:p-6">
                        <h2 className="font-semibold">Needs your attention</h2>
                        {attention.length === 0 ? (
                            <p className="py-8 text-center text-sm text-muted-foreground">
                                Nothing is waiting on you.
                            </p>
                        ) : (
                            <ul className="mt-3 divide-y divide-border">
                                {attention.map((item) => (
                                    <li key={item.id}>
                                        <Link
                                            to={`/stock/procurement/purchase-orders/${item.id}`}
                                            className="flex items-start gap-3 py-3 -mx-2 px-2 rounded-md hover:bg-accent/40"
                                        >
                                            <span
                                                className={cn(
                                                    'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                                                    ATTENTION_DOT[item.kind] ?? 'bg-gray-400'
                                                )}
                                            />
                                            <span className="min-w-0 flex-1">
                                                <span className="block text-sm font-medium truncate">
                                                    {item.title}
                                                </span>
                                                <span className="block text-xs text-muted-foreground truncate">
                                                    {item.subtitle}
                                                </span>
                                            </span>
                                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                                                {item.meta}
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-4 md:p-6">
                        <h2 className="font-semibold">Recent activity</h2>
                        {recentActivity.length === 0 ? (
                            <p className="py-8 text-center text-sm text-muted-foreground">
                                No purchase order activity yet.
                            </p>
                        ) : (
                            <ul className="mt-3 space-y-3">
                                {recentActivity.map((entry) => (
                                    <li key={entry.id} className="flex flex-col gap-0.5">
                                        <span className="text-sm">{entry.text}</span>
                                        <span className="text-xs text-muted-foreground">
                                            {entry.actorName} · {formatDateTime(entry.createdAt)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
